import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import App from './App.tsx';
import { applyStoredConsent, captureGclidFromUrl, initAnalytics } from './analytics.ts';
import './styles/global.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

// Prefetch /api/config once, before booting React, so Sentry can initialize early enough to
// capture render-time errors. The endpoint is cheap (single in-memory read), responds in ~1ms
// from the server we just connected to, and the SPA shell HTML is already on the page so the
// user doesn't see a delay. If the call fails, we boot without Sentry rather than blocking the
// app — error tracking is best-effort, the product comes first.
type PublicConfig = {
  googleClientId?: string;
  stripePublishableKey?: string;
  sentryDsn?: string;
  sentryEnvironment?: string;
};

function initSentry(config: PublicConfig) {
  if (!config.sentryDsn) return;
  Sentry.init({
    dsn: config.sentryDsn,
    environment: config.sentryEnvironment,
    // No replay / no performance — keep the bundle small and the quota lean. Errors only.
    integrations: [],
    tracesSampleRate: 0,
    // Drop noise: extension-injected scripts, network blips, ResizeObserver loops.
    ignoreErrors: [
      'ResizeObserver loop',
      'NetworkError when attempting to fetch resource',
      'Failed to fetch',
      'Load failed',
    ],
  });
}

async function bootstrap() {
  try {
    const res = await fetch('/api/config', { credentials: 'include' });
    if (res.ok) {
      const cfg = (await res.json()) as PublicConfig;
      initSentry(cfg);
      (window as unknown as { __EAA_CONFIG__?: PublicConfig }).__EAA_CONFIG__ = cfg;
    }
  } catch {
    // Best-effort. Boot without Sentry if /api/config is unreachable.
  }

  // Analytics + ad attribution bootstrap. These run before React mounts so the gtag script
  // is fetching while the SPA renders, and a ?gclid= on the landing URL is captured into
  // the cookie before the URL is replaced by client-side routing.
  //
  // applyStoredConsent runs FIRST so returning visitors who previously accepted have their
  // consent applied to gtag BEFORE the first auto-page_view fires inside initAnalytics().
  applyStoredConsent();
  initAnalytics();
  captureGclidFromUrl();

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
}

bootstrap();
